"use client";

import { useEffect, useRef, useState } from "react";
import { DollarSign } from "lucide-react";
import { useFeatureFlag } from "@/hooks/use-feature-flags";

interface WinningTrade {
  symbol: string;
  strategy: string;
  profit: number;
  roi: number | null;
  closedAt: string;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function orderByDateShuffled(trades: WinningTrade[]): WinningTrade[] {
  const byDate = new Map<string, WinningTrade[]>();
  for (const t of trades) {
    const arr = byDate.get(t.closedAt) || [];
    arr.push(t);
    byDate.set(t.closedAt, arr);
  }
  const dates = Array.from(byDate.keys()).sort((a, b) => b.localeCompare(a));
  const result: WinningTrade[] = [];
  for (const date of dates) {
    result.push(...shuffle(byDate.get(date)!));
  }
  return result;
}

function formatProfit(n: number): string {
  if (n >= 1000) {
    return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  }
  return `$${Math.round(n).toLocaleString()}`;
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const BUBBLE_VISIBLE_MS = 5500;
const FADE_OUT_MS = 500;
const MIN_INTERVAL_MS = 8000;
const MAX_INTERVAL_MS = 15000;

function randomInterval() {
  return MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
}

interface BubbleState {
  trade: WinningTrade;
  key: number;
  exiting: boolean;
}

export default function WinningTradeBubbles() {
  const enabled = useFeatureFlag("winningTradeBubbles");
  const [trades, setTrades] = useState<WinningTrade[]>([]);
  const [bubble, setBubble] = useState<BubbleState | null>(null);
  const indexRef = useRef(0);
  const keyRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    fetch("/api/public/stats")
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const t = data.recentWinningTrades;
        if (Array.isArray(t) && t.length > 0) {
          setTrades(orderByDateShuffled(t));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled]);

  useEffect(() => {
    if (trades.length === 0) return;

    let exitTimer: ReturnType<typeof setTimeout>;
    let removeTimer: ReturnType<typeof setTimeout>;
    let nextTimer: ReturnType<typeof setTimeout>;
    let cancelled = false;

    function showNext() {
      if (cancelled) return;

      if (indexRef.current > 0 && indexRef.current % trades.length === 0) {
        indexRef.current = 0;
        setTrades((prev) => orderByDateShuffled(prev));
      }

      const trade = trades[indexRef.current % trades.length];
      indexRef.current++;
      const k = ++keyRef.current;

      setBubble({ trade, key: k, exiting: false });

      exitTimer = setTimeout(() => {
        if (cancelled) return;
        setBubble((prev) => (prev && prev.key === k ? { ...prev, exiting: true } : prev));
      }, BUBBLE_VISIBLE_MS);

      removeTimer = setTimeout(() => {
        if (cancelled) return;
        setBubble((prev) => (prev && prev.key === k ? null : prev));
      }, BUBBLE_VISIBLE_MS + FADE_OUT_MS);

      nextTimer = setTimeout(showNext, randomInterval());
    }

    const initialDelay = setTimeout(showNext, 3000 + Math.random() * 2000);

    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
      clearTimeout(exitTimer);
      clearTimeout(removeTimer);
      clearTimeout(nextTimer);
    };
  }, [trades]);

  if (!bubble) return null;

  const { trade, key, exiting } = bubble;

  return (
    <div
      className="fixed right-2 sm:right-4 bottom-4 sm:bottom-20 z-40 pointer-events-none"
      aria-live="polite"
    >
      <div
        key={key}
        className={`max-w-[200px] sm:max-w-[280px] rounded-xl border border-border/40 bg-card/90 backdrop-blur-sm shadow-lg p-2.5 sm:p-4 ${
          exiting ? "animate-trade-bubble-out" : "animate-trade-bubble-in"
        }`}
      >
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 mt-0.5 rounded-full bg-emerald-500/15 p-1.5">
            <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-400" />
          </div>
          <div className="min-w-0">
            <p className="text-xs sm:text-sm text-foreground leading-snug">
              A trader made{" "}
              <span className="font-medium text-emerald-400">
                {formatProfit(trade.profit)}
              </span>{" "}
              on{" "}
              <span className="font-medium">{trade.symbol}</span>
            </p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className="inline-block rounded-full bg-muted/60 px-2 py-0.5 text-[10px] sm:text-xs text-muted-foreground">
                {trade.strategy}
              </span>
              {trade.roi != null && trade.roi > 0 && trade.roi < 1000 && (
                <span className="text-[10px] sm:text-xs font-medium text-emerald-400">
                  +{trade.roi > 999 ? "999+" : trade.roi.toFixed(1)}% ROI
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
